import _ from 'lodash';
import {prop} from '@packages/utilities/fp/utils';
import {pickBy} from '@packages/utilities/key-map';
import {computedValue} from '@packages/shared/stores/StyleSystem/renderers';
import {
  WIDTH_UNIT_CONVERSIONS,
  HEIGHT_UNIT_CONVERSIONS,
  FONT_SIZE_UNIT_CONVERSIONS,
} from './CssUnitUtils';

import {
  StylePath,
  type StyleStateType,
} from '@packages/systems/style-legacy/values';
import {
  type CssPropertyDetail,
  CSS_PROPERTY_DETAILS as SHARED_CSS_PROPERTY_DETAILS,
} from '@packages/systems/style-legacy/css-properties';
import type {WebflowCssProperty} from '@packages/systems/core/utils/WebflowCssSubset';
import {isCssVariableProperty} from '@packages/systems/core/utils/CssStringUtils';

const getBrowserPropertyPrefix = (
  propName: string,
  expected: string
): string | false | undefined => {
  // HACK: `document` will be undefined in some test environments:
  if (!global.document) {
    return false;
  }

  const tempDiv = global.document.createElement('div');
  const tempStyle = tempDiv.style;
  const webkitProp = '-webkit-' + propName;

  tempStyle.cssText =
    webkitProp + ': ' + expected + '; ' + propName + ': ' + expected + ';';

  // @ts-expect-error - TS7015 - Element implicitly has an 'any' type because index expression is not of type 'number'.
  if (String(tempStyle[_.camelCase(propName)]) === expected) {
    return false;
  }

  // @ts-expect-error - TS7015 - Element implicitly has an 'any' type because index expression is not of type 'number'.
  if (String(tempStyle[_.camelCase(webkitProp)]) === expected) {
    return '-webkit-';
  }
};
const PREFIX_BACKFACE_VISIBILITY = getBrowserPropertyPrefix(
  'backface-visibility',
  'visible'
);

export const CSS_PROPERTY_DETAILS = _.merge({}, SHARED_CSS_PROPERTY_DETAILS, {
  'font-size': {
    conversions: FONT_SIZE_UNIT_CONVERSIONS,
  },
  height: {
    conversions: HEIGHT_UNIT_CONVERSIONS,
  },
  'line-height': {
    conversions: FONT_SIZE_UNIT_CONVERSIONS,
  },
  'max-height': {
    conversions: HEIGHT_UNIT_CONVERSIONS,
  },

  'max-width': {
    conversions: WIDTH_UNIT_CONVERSIONS,
  },

  'min-height': {
    conversions: HEIGHT_UNIT_CONVERSIONS,
  },

  'min-width': {
    conversions: WIDTH_UNIT_CONVERSIONS,
  },
  width: {
    conversions: WIDTH_UNIT_CONVERSIONS,
  },
  'backface-visibility': {
    // Prefix still required by Safari 11: https://caniuse.com/#search=perspective
    prefix: PREFIX_BACKFACE_VISIBILITY,
  },
});

const PROPERTY_NAMES: Record<string, keyof typeof CSS_PROPERTY_DETAILS> =
  (function () {
    const o: Partial<Record<string, keyof typeof CSS_PROPERTY_DETAILS>> = {};

    for (const k in CSS_PROPERTY_DETAILS) {
      // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'Partial<Record<string, CssPropertyDetail>>'.
      const path: string = CSS_PROPERTY_DETAILS[k].path;
      !(path in o) && (o[path] = k as keyof typeof CSS_PROPERTY_DETAILS);
    }

    return o as Record<string, keyof typeof CSS_PROPERTY_DETAILS>;
  })();

const PROPERTY_NAME_REGEXP_SPECS = [
  {
    propName: 'background',
    regexp: /^backgroundImages\.(?:local|upstream)\.value(?:\.[0-9]+)?$/,
  },
  {
    propName: 'background-position',
    regexp:
      /^backgroundImages\.(?:local|upstream)\.value\.[0-9]+\.(?:top|left)$/,
  },
  {
    propName: 'background-size',
    regexp:
      /^backgroundImages\.(?:local|upstream)\.value\.[0-9]+\.(?:width|height)$/,
  },
  {
    propName: 'background-attachment',
    regexp: /^backgroundImages\.(?:local|upstream)\.value\.[0-9]+\.attachment$/,
  },
  {
    propName: 'background-repeat',
    regexp: /^backgroundImages\.(?:local|upstream)\.value\.[0-9]+\.repeat$/,
  },
  {
    propName: 'box-shadow',
    regexp: /^boxShadows\.(?:local|upstream)\.value\.[0-9]+/,
  },
  {
    propName: 'text-shadow',
    regexp: /^textShadows\.(?:local|upstream)\.value\.[0-9]+/,
  },
  {
    propName: 'transition-property',
    regexp: /^transitions\.(?:local|upstream)\.value\.[0-9]+\.property$/,
  },
  {
    propName: 'transition-delay',
    regexp: /^transitions\.(?:local|upstream)\.value\.[0-9]+\.delay$/,
  },
  {
    propName: 'transition-timing-function',
    regexp: /^transitions\.(?:local|upstream)\.value\.[0-9]+\.timing$/,
  },
  {
    propName: 'transform',
    regexp: /^transforms\.(?:local|upstream)\.value\.[0-9]+/,
  },
  {
    propName: 'filter',
    regexp: /^filters\.(?:local|upstream)\.value\.[0-9]+/,
  },
  {
    propName: 'backdrop-filter',
    regexp: /^backdropFilters\.(?:local|upstream)\.value\.[0-9]+/,
  },
  {
    propName: 'font-variation-settings',
    regexp: /^fontVariationSettings\.(?:local|upstream)\.value\.[0-9]+/,
  },
];

export const MULTI_PROPERTY_PATHS = [
  'backgroundImages',
  'boxShadows',
  'textShadows',
  'filters',
  'backdropFilters',
  'fontVariationSettings',
  'transforms',
  'transitions',
] as const;

export const BGIMAGE_SUBVALUE_PROPS = [
  'background-position',
  'background-size',
  'background-repeat',
  'background-attachment',
] as const;

export const BGIMAGE_ALL_PROPS = [
  'background-image',
  ...BGIMAGE_SUBVALUE_PROPS,
] as const;

export const TRANSITION_SUBVALUE_PROPS = [
  'transition-property',
  'transition-duration',
  'transition-timing-function',
] as const;

/**
 * Given a CSS Variable property name, generate a `CssPropertyDetail` object
 * that describes a generic CSS variable.
 */
export function cssPropertyDetailForCSSVariable(
  cssName: `--${string}`
): CssPropertyDetail {
  return {
    prop: cssName,
    path: cssName,
    // We use "typographic" to signify that a property is inherited, so we use
    // it here for all CSS variables since they are also inherited (at least
    // with the way we currently author them -- @property/registerProperty
    // allows this to be configured but there is not widespread browser
    // support yet so we don't have plans to support this.)
    typographic: true,
  };
}

/**
 * Looks up the property details for a specific CSS property.
 * @param  {String} cssName The name of the CSS property.
 * @return {Object}         The details of the property, or undefined.
 */
export function cssPropDetails(
  cssName: WebflowCssProperty | `--${string}`
): CssPropertyDetail | null {
  if (isCssVariableProperty(cssName)) {
    return cssPropertyDetailForCSSVariable(cssName);
  }
  // @ts-expect-error - Property 'background-repeat-x' does not exist on CSS_PROPERTY_DETAILS ...
  return CSS_PROPERTY_DETAILS[cssName] ?? null;
}

export function cssPropDetailsFromNativeProperty(
  cssName: string
): CssPropertyDetail | null {
  if (isCssVariableProperty(cssName)) {
    return cssPropertyDetailForCSSVariable(cssName);
  }

  if (cssName in CSS_PROPERTY_DETAILS) {
    return CSS_PROPERTY_DETAILS[cssName as keyof typeof CSS_PROPERTY_DETAILS];
  }
  return null;
}

/**
 * Looks up the CSS property name for a specific model path.
 * @param  {String} path  The dot-separated model path, e.g. "margin.top"
 * @return {String}       The CSS property name, e.g. "margin-top"
 */
export function cssPropFromPath(path: string) {
  if (isCssVariableProperty(path)) {
    return path;
  }

  const propertyNameRegexpSpec = _.find(
    PROPERTY_NAME_REGEXP_SPECS,
    function (regexpSpec) {
      const regexp = regexpSpec.regexp;
      return regexp.test(path);
    }
  );
  if (propertyNameRegexpSpec) {
    const propName = propertyNameRegexpSpec.propName;
    return propName;
  } else {
    return PROPERTY_NAMES[path];
  }
}

/**
 * Looks up property details for a dot-separated model path.
 * @param  {String} path The dot-separated model path, e.g. "margin.top"
 * @return {Object}      The details of the CSS property from CSS_PROPERTY_DETAILS
 */
export function cssPropDetailsFromPath(path: string) {
  const propName = cssPropFromPath(path);
  if (propName == null) return null;
  return cssPropDetails(
    // @ts-expect-error - `propName` is a plain string
    propName
  );
}

export const inheritedProperties: Set<WebflowCssProperty> = new Set<any>(
  Object.keys(pickBy(prop('typographic'))(CSS_PROPERTY_DETAILS))
);

export function calculateDefaultValueForPath(
  path: string,
  styleStoreState: StyleStateType,
  node: HTMLElement
) {
  const ONE_DEFAULTS = [
    'opacity',
    'columnGap',
    'fontSize',
    'letterSpacing',
    'textIndent',
  ];
  const EXTRA_DEFAULT_STRINGS = {
    perspective: 1000,
  } as const;
  const EXTRA_DEFAULT_REGEXPS = [
    {
      regexp: /^transforms\.(?:local|upstream)\.value\.[0-9]+\.perspective/,
      value: 1000,
    },
  ];

  function _getDefaultStringMatch(_path: string): number | null {
    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly perspective: 1000; }'.
    const match = EXTRA_DEFAULT_STRINGS[_path];
    return match === undefined ? null : match;
  }

  function _getDefaultRegexpMatch(_path: string): number | null {
    const match = _.find(EXTRA_DEFAULT_REGEXPS, (regexpSpec) =>
      regexpSpec.regexp.test(_path)
    );
    return match ? match.value : null;
  }

  let match;

  if (path === 'width' || path === 'maxWidth') {
    return node.getBoundingClientRect().width || 0;
  } else if (path === 'minWidth' || path === 'minHeight') {
    return 0;
  } else if (path === 'height' || path === 'maxHeight') {
    return node.getBoundingClientRect().height || 0;
  } else if (
    (match = path.match(
      /^(backgroundImages\.(?:local|upstream)\.value\.[0-9]+)\.width$/
    ))
  ) {
    return computedValue(
      _.get(styleStoreState, StylePath.expandPath(match[1] + '.sourceWidth'))
    );
  } else if (
    (match = path.match(
      /^(backgroundImages\.(?:local|upstream)\.value\.[0-9]+)\.height$/
    ))
  ) {
    return computedValue(
      _.get(styleStoreState, StylePath.expandPath(match[1] + '.sourceHeight'))
    );
  } else if (ONE_DEFAULTS.indexOf(path) !== -1) {
    return 1;
  } else if ((match = _getDefaultStringMatch(path))) {
    return match;
  } else if ((match = _getDefaultRegexpMatch(path))) {
    return match;
  } else {
    return null;
  }
}
